import { cva, VariantProps } from 'class-variance-authority';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/sk';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import { Locale } from 'next-intl';
import { type ReactNode, useMemo } from 'react';
import { FormattedVenueOpeningHour } from '@/entities/venue/lib/get-formatted-venue-opening-hours.util';
import { groupFormattedVenueOpeningHours } from '@/entities/venue/lib/group-formatted-venue-opening-hours.util';
import { getClosedCopyText, getOutageReturnText } from '@/entities/venue/lib/venue-availability-text.utils';
import type { VenueAvailabilityStatus } from '@/entities/venue/lib/venue-availability.utils';
import type { Translate } from '@/shared/i18n/translate';
import { cn } from '@/shared/lib/cn';
import { getLocaleTime } from '@/shared/lib/format-locale-date-time.util';
import { WithDataAttributes } from '@/shared/model/WithDataAttributes';
import './venue-status.scss';

dayjs.extend(isoWeek);
dayjs.extend(localeData);

type VenueStatusEveryOpeningHourProps = {
    openingHours: FormattedVenueOpeningHour[];
    locale: string;
    t: Translate;
};
const VenueStatusEveryOpeningHour = ({ openingHours, locale, t }: VenueStatusEveryOpeningHourProps) => {
    const groupedHours = useMemo(
        () => groupFormattedVenueOpeningHours(openingHours, locale),
        [openingHours, locale],
    );

    return (
        <div className="venue-status__opening-hours">
            {groupedHours.map(({ dayRange, intervals }, index) => (
                <span key={index}>
                    {`${dayRange} ${
                        intervals.length === 0
                            ? t('VENUE_CARD.CLOSED')
                            : intervals.map((interval) => `${interval.start} - ${interval.end}`).join(', ')
                    }`}
                </span>
            ))}
        </div>
    );
};

const venueStatusVariants = cva('venue-status', {
    variants: {
        variant: {
            default: 'venue-status--default',
            everyOpeningHour: 'venue-status--every-opening-hour',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

export type VenueStatusProps = WithDataAttributes<
    VariantProps<typeof venueStatusVariants> & {
        openingHours: FormattedVenueOpeningHour[];
        status: VenueAvailabilityStatus;
        t: Translate;
        locale: Locale;
    }
>;
export const VenueStatus = ({ openingHours, variant, status, t, locale, ...props }: VenueStatusProps) => {
    const isOpen = status.kind === 'open';
    const statusLabel =
        status.kind === 'outage'
            ? t('VENUE_CARD.SUSPENDED')
            : isOpen
              ? t('VENUE_CARD.OPEN')
              : t('VENUE_CARD.CLOSED');

    let detail: ReactNode = null;
    if (status.kind === 'open') {
        detail = (
            <span>
                ·{' '}
                {t(`VENUE_CARD.CLOSING_${status.closesAtDayDiff >= 1 ? 'TOMORROW_' : ''}AT`, {
                    time: getLocaleTime(status.closesAtMs, locale),
                })}
            </span>
        );
    } else if (status.kind === 'outage') {
        const text = getOutageReturnText(status.outageReturn, status.endsAtDayjs.toDate(), locale, t, {
            minutes: 'VENUE_CARD.OPENING_IN',
            soon: 'VENUE_CARD.OPENING_SOON',
            at: 'VENUE_CARD.OPENING_AT',
            tomorrowAt: 'VENUE_CARD.OPENING_TOMORROW_AT',
            untilDate: 'VENUE_CARD.OPENING_UNTIL_DATE',
        });
        detail = <span>· {text}</span>;
    } else if (status.kind === 'closed') {
        const text = getClosedCopyText(status, locale, t, {
            today: 'VENUE_CARD.OPENING_TODAY_AT',
            tomorrow: 'VENUE_CARD.OPENING_TOMORROW_AT',
            later: 'VENUE_CARD.OPENING_ON_DAY_AT',
        });
        detail = <span>· {text}</span>;
    }

    return (
        <div className={cn(venueStatusVariants({ variant }), !isOpen && 'venue-status--closed')} {...props}>
            <span className={cn('venue-status__text', !isOpen && 'venue-status__text--closed')}>
                {statusLabel}
            </span>
            {detail}
            {variant === 'everyOpeningHour' && (
                <VenueStatusEveryOpeningHour openingHours={openingHours} locale={locale} t={t} />
            )}
        </div>
    );
};
